import React, { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';

import './index.less';

import {
  getApiAgentsPath,
  getApiKeysPath,
  getAsrPagePath,
  getChatAgentsPath,
  getFoldersPath,
  getMoreItemsPath,
  getOcrPagePath,
  getProjectsPath,
  getProvidersPath,
  getToolkitsPath,
} from '../../../utils/pathUtils';
import { currentFolderIdSelector, selectedFolderIdSelector } from '../../../recoil';
import IbMenuButton from '../../../components/common/IbMenuButton';
import IbIcon, { IbIconName } from '../../../components/common/IbIcon';
import IbContextMenu, { IIbContextMenuItem } from '../../../components/common/IbContextMenu';
import IbDivider from '../../../components/common/IbDivider';
import FolderSelector from '../../../components/FolderSelector';

import { DIVIDER_CLASS_NAME, GROUP_CLASS_NAME, MAIN_CLASS_NAME, MOBILE_CLASS_NAME } from './const';

type IIbMainMenuItem = { displayName: string; iconName: IbIconName; menuItemCode: string };

interface IIbMainMenuProps {
  selectedItem: string;
}

const IbMainMenu: React.FC<IIbMainMenuProps> = ({ selectedItem }) => {
  const { t } = useTranslation();

  // NOTE: может быть undefined при рендеринге в Storybook
  const history = useHistory?.();
  const location = useLocation();

  const { folderId } = useParams<{ folderId?: string }>();

  const [selectedFolderId, setSelectedFolderId] = useRecoilState(selectedFolderIdSelector);
  const currentFolderId = useRecoilValue(currentFolderIdSelector);

  const [contextMenuVisible, setContextMenuVisible] = useState(false);

  const onParamFolderIdChange = () => {
    if (!folderId) {
      return;
    }

    setSelectedFolderId(folderId);
  };

  const onSelectedFolderIdChange = () => {
    if (!selectedFolderId) {
      return;
    }

    const prefix = '/folders/';
    if (!location.pathname.startsWith(prefix)) {
      return;
    }

    const path = location.pathname.replace(/^\/folders\/[^/]+/, `${prefix}${selectedFolderId}`);
    if (path === location.pathname) {
      return;
    }

    history.push(path);
  };

  useEffect(onParamFolderIdChange, [folderId]);
  useEffect(onSelectedFolderIdChange, [selectedFolderId]);

  const desktopMenuGroups: IIbMainMenuItem[][] = [
    [
      { displayName: t('Agents'), iconName: 'user-business', menuItemCode: getChatAgentsPath(currentFolderId) },
      { displayName: t('Actions'), iconName: 'worker', menuItemCode: getApiAgentsPath(currentFolderId) },
      { displayName: t('Projects'), iconName: 'optimize', menuItemCode: getProjectsPath(currentFolderId) },
      { displayName: t('Toolkits'), iconName: 'toolkit', menuItemCode: getToolkitsPath(currentFolderId) },
    ],
    [
      { displayName: t('Folders'), iconName: 'folder-plus', menuItemCode: getFoldersPath() },
      { displayName: t('Providers'), iconName: 'server', menuItemCode: getProvidersPath() },
      { displayName: t('OCR-page'), iconName: 'text-recognition', menuItemCode: getOcrPagePath() },
      { displayName: t('ASR-page'), iconName: 'audio-file', menuItemCode: getAsrPagePath() },
      { displayName: t('API-keys'), iconName: 'key', menuItemCode: getApiKeysPath() },
    ],
  ];

  const mobileMenuItems: IIbMainMenuItem[] = [
    { displayName: t('Agents'), iconName: 'user-business', menuItemCode: getChatAgentsPath(currentFolderId) },
    { displayName: t('Actions'), iconName: 'worker', menuItemCode: getApiAgentsPath(currentFolderId) },
    { displayName: t('Projects'), iconName: 'optimize', menuItemCode: getProjectsPath(currentFolderId) },
    { displayName: t('Toolkits'), iconName: 'toolkit', menuItemCode: getToolkitsPath(currentFolderId) },
    { displayName: t('Folders'), iconName: 'folder-plus', menuItemCode: getFoldersPath() },
    { displayName: t('Providers'), iconName: 'server', menuItemCode: getProvidersPath() },
    { displayName: t('OCR-page'), iconName: 'text-recognition', menuItemCode: getOcrPagePath() },
    { displayName: t('ASR-page'), iconName: 'audio-file', menuItemCode: getAsrPagePath() },
    { displayName: t('API-keys'), iconName: 'key', menuItemCode: getApiKeysPath() },
    { displayName: t('More'), iconName: 'hamburger-button', menuItemCode: getMoreItemsPath() },
  ];

  // TODO: вместо projects добавлять другие разделы по мере их появления.
  const contextMenuItems = [
    {
      icon: <IbIcon iconName="optimize" />,
      text: t('Projects'),
      onSelect: () => {
        history?.push(getProjectsPath(currentFolderId));
      },
    },
  ] as IIbContextMenuItem[];

  const onMenuItemClick = (selectedItem: string) => () => {
    history?.push(selectedItem);
  };

  const onMoreMenuItemClick = () => {
    setContextMenuVisible(true);
  };

  const onContextMenuClose = () => setContextMenuVisible(false);

  return (
    <>
      <div className={MAIN_CLASS_NAME}>
        <FolderSelector />
        {desktopMenuGroups.map((group, index) => (
          <Fragment key={index}>
            <div className={GROUP_CLASS_NAME}>
              {group.map((item) => (
                <IbMenuButton
                  key={item.menuItemCode}
                  icon={<IbIcon iconName={item.iconName} />}
                  selected={selectedItem == item.menuItemCode}
                  text={item.displayName}
                  onClick={onMenuItemClick(item.menuItemCode)}
                />
              ))}
            </div>
            <IbDivider className={DIVIDER_CLASS_NAME} />
          </Fragment>
        ))}
      </div>
      <div className={MOBILE_CLASS_NAME}>
        {mobileMenuItems.map((item) => {
          return item.menuItemCode === getMoreItemsPath() ? (
            <IbContextMenu
              key={item.menuItemCode}
              menuItems={contextMenuItems}
              visible={contextMenuVisible}
              onClose={onContextMenuClose}
            >
              <IbMenuButton
                icon={<IbIcon iconName={item.iconName} />}
                selected={!mobileMenuItems.some((i) => i.menuItemCode === selectedItem)}
                text={item.displayName}
                onClick={onMoreMenuItemClick}
              />
            </IbContextMenu>
          ) : (
            <IbMenuButton
              key={item.menuItemCode}
              icon={<IbIcon iconName={item.iconName} />}
              selected={selectedItem === item.menuItemCode}
              text={item.displayName}
              onClick={onMenuItemClick(item.menuItemCode)}
            />
          );
        })}
      </div>
    </>
  );
};

export default IbMainMenu;
